import * as XLSX from 'xlsx';

export const loadXLSForm = (file, callback) => {
  const reader = new FileReader();

  reader.onload = (event) => {
    let workbook = null;
    try {
      const data = new Uint8Array(event.target.result);
      workbook = XLSX.read(data, { type: 'array' });
    } catch (error) {
      callback(new Error(`Error reading the XLSX file: ${error.message || error}`), null);
      return;
    }

    // Check if both "survey" and "choices" worksheets are present
    if (!workbook.SheetNames.includes("survey") || !workbook.SheetNames.includes("choices")) {
      callback(new Error("Must be XLSForm file with both 'survey' and 'choices' worksheets present"), null);
      return;
    }

    const surveyWorksheet = workbook.Sheets.survey;
    const choicesWorksheet = workbook.Sheets.choices;
    const settingsWorksheet = workbook.Sheets.settings;

    const surveyData = dropEmptyColumnsAndRows(XLSX.utils.sheet_to_json(surveyWorksheet, { header: 1 }));
    const choicesData = dropEmptyColumnsAndRows(XLSX.utils.sheet_to_json(choicesWorksheet, { header: 1 }));
    const settingsData = dropEmptyColumnsAndRows(XLSX.utils.sheet_to_json(settingsWorksheet, { header: 1 }));

    // settings worksheet: convert arrays to objects with property names from the first row
    const settingsDataWithHeaders = settingsData.map(row => {
      const obj = {};
      row.forEach((value, index) => {
        obj[settingsData[0][index]] = value;
      });
      return obj;
    }).slice(1);  // exclude the first row

    // get the default language from the first row of the settings worksheet's "default_language" column
    const defaultLanguage = settingsDataWithHeaders.find(row => row.default_language)?.default_language
        || "English";

    // survey worksheet: convert arrays to objects with property names from the first row
    const surveyDataWithHeaders = surveyData.map(row => {
      const obj = {};
      row.forEach((value, index) => {
        let key = surveyData[0][index];
        if (key) {
          // adjustment: remove language suffixes for default language name (when it won't create a conflict)
          const parsedColumn = separateColumnFromLanguage(key);
          if (parsedColumn.language === defaultLanguage && !surveyData[0].includes(parsedColumn.column)) {
            key = parsedColumn.column;
          }
          obj[key] = value;
        }
      });
      return obj;
    }).slice(1);  // exclude the first row

    // choices worksheet: convert arrays to objects with property names from the first row
    const choicesDataWithHeaders = choicesData.map(row => {
      const obj = {};
      row.forEach((value, index) => {
        let key = choicesData[0][index];
        if (key) {
          // adjustment: if there's a "name" column but no "value" column, rename "name" to "value"
          if (key === "name" && !choicesData[0].includes("value")) {
            key = "value";
          }
          // adjustment: remove language suffixes for default language name (when it won't create a conflict)
          const parsedColumn = separateColumnFromLanguage(key);
          if (parsedColumn.language === defaultLanguage && !surveyData[0].includes(parsedColumn.column)) {
            key = parsedColumn.column;
          }
          obj[key] = value;
        }
      });
      return obj;
    }).slice(1);  // exclude the first row

    // parse survey items
    const surveyItems = parseSurveyData(surveyDataWithHeaders, choicesDataWithHeaders);

    // condense raw data for return
    const rawSurveySheet = filterEmptyValues(surveyDataWithHeaders);
    const rawChoicesSheet = filterEmptyValues(choicesDataWithHeaders);
    const rawSettingsSheet = filterEmptyValues(settingsDataWithHeaders);
    // drop the public_key column, if any, from rawSettingsSheet, for safety
    rawSettingsSheet.forEach(row => delete row.public_key);

    callback(null, {defaultLanguage, surveyItems, rawSurveySheet, rawChoicesSheet, rawSettingsSheet});
  };

  reader.readAsArrayBuffer(file);
};

function dropEmptyColumnsAndRows(data) {
  // first, filter out columns where the header is null or empty
  const headers = data[0];
  const validColumns = headers.reduce((acc, header, index) => {
    if (header != null && header.trim() !== '') {
      acc.push(index);
    }
    return acc;
  }, []);

  // then, map through the rows
  return data.map((row, _) => {
    // for each row, only keep the cells that belong to a valid column
    const filteredRow = validColumns.map(columnIndex => row[columnIndex]);

    // if the row is not empty (i.e., it has at least one non-null, non-empty cell), keep it
    return filteredRow.some(value => value != null && value.toString().trim() !== '') ? filteredRow : null;
  }).filter(Boolean);  // finally, filter out null values (i.e., empty rows)
}

const filterEmptyValues = (data) => {
  return data.map(row => {
    return Object.fromEntries(
      Object.entries(row).filter(([_, value]) => value !== null && value !== '')
    );
  });
};

function parseSurveyData(surveyData, choicesData) {
  const surveyItems = [];
  const groupStack = [];

  for (const row of surveyData) {
    if (row.type && row.name) {
      const type = row.type.trim();
      const name = row.name.trim();

      if (type === "begin group" || type === "begin repeat") {
        const isRepeated = type === "begin repeat";
        groupStack.push({name, isRepeated});
      } else if (type === "end group" || type === "end repeat") {
        groupStack.pop();
      } else if (type) {
        // add the survey item, including survey sheet details and enclosing group info
        const item = {...row, groups: [...groupStack]};

        // if the type is "select_one" or "select_multiple", add the choices
        const match = type.match(/^(select_one|select_multiple) (.+)$/);
        if (match) {
          const listName = match[2];
          item.choices = choicesData.filter(choice => choice.list_name === listName)
              // eslint-disable-next-line camelcase
                                    .map(({list_name, ...rest}) => rest);
        }

        // parse translations - survey sheet
        item.translations = [];
        for (const key in row) {
          if (key && row[key]) {
            const parsedColumn = separateColumnFromLanguage(key);
            if (parsedColumn.language) {
              let translation = item.translations.find(t => t.language === parsedColumn.language);
              if (!translation) {
                translation = {language: parsedColumn.language};
                item.translations.push(translation);
              }
              translation[parsedColumn.column] = row[key];
              // and delete the original column
              delete item[key];
            }
          }
        }

        // parse translations - choices sheet
        if (item.choices) {
          for (const choice of item.choices) {
            for (const translation of item.translations) {
              const translatedChoice = {...choice};
              const labelKey = `label:${translation.language}`;
              if (translatedChoice[labelKey]) {
                translatedChoice.label = translatedChoice[labelKey];
                delete translatedChoice[labelKey];
                delete choice[labelKey]
              }
              for (const key in translatedChoice) {
                if (key.startsWith("label:")) {
                  delete translatedChoice[key];
                }
              }
              if (!translation.choices) {
                  translation.choices = [];
              }
              translation.choices.push(translatedChoice);
            }
          }
        }

        // save the survey item in the list
        surveyItems.push(item);
      }
    }
  }

  return surveyItems;
}

export function formFieldList(fileData) {
  const formFields = [];

  // cheapest version doesn't list anything
  // return '';

  // next cheapest version lists only the field names
  // fileData.surveyItems.forEach(item => {
  //   let fieldName = item.name;
  //
  //   const repeatedGroups = item.groups
  //     .filter(group => group.isRepeated)
  //     .map(group => group.name);
  //
  //   if (repeatedGroups.length > 0) {
  //     fieldName += ` (type: ${item.type}; repeated within: ${repeatedGroups.join(', ')})`;
  //   }
  //   else {
  //     fieldName += ` (type: ${item.type})`;
  //   }
  //
  //   formFields.push(fieldName);
  // });
  //
  // return formFields.join(', ');

  // best and most expensive version lists the field names, field labels, choice labels, and choices
  fileData.surveyItems.forEach(item => {
      // fetch enclosing repeat groups (if any)
      const repeatedGroups = item.groups
        .filter(group => group.isRepeated)
        .map(group => `\`${group.name}\``);

      // strip HTML tags and linebreaks from the label
      let cleanLabel =
        item.label ? item.label.toString().replace(/<[^>]*>/g, '').replace(/\r?\n|\r/g, ' ') : '';

      // ensure that we begin on a new line
      if (formFields.length > 0) {
        formFields.push('');
      }

      // then start a bullet with the field name
      let fieldDetails = `* \`${item.name}\``;

      // add type, repeat-group details (if any), and label
      if (repeatedGroups.length > 0) {
        fieldDetails += ` (type: \`${item.type}\`; repeated within: ${repeatedGroups.join(', ')}) - ${cleanLabel}`;
      }
      else {
        fieldDetails += ` (type: \`${item.type}\`) - ${cleanLabel}`;
      }

      // push the field details
      formFields.push(fieldDetails);

      // finally, add sub-bullets for choices (if any)
      if (Array.isArray(item.choices)) {
        item.choices.forEach(choice => {
          cleanLabel = choice.label ? choice.label.toString().replace(/<[^>]*>/g, '').replace(/\r?\n|\r/g, ' ') : '';
          formFields.push(`  * \`${cleanLabel}\` = \`${choice.value}\``);
        });
      }
  });

  return formFields.join('\n');
}

function separateColumnFromLanguage(key) {
  let column = key;
  let language = null;

  if (key) {
    const match = key.match(/(.*):([^:]+)$/);
    if (match) {
      const candidateColumn = match[1].trim();
      if (candidateColumn !== "media") {
        column = candidateColumn;
        language = match[2].trim();
      }
    }
  }

  return { column, language };
}
